import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { searchWorkflowTasks, SearchWorkflowTasksResponse } from "../../../../../service/query";
import { Status } from "../../../../../service/Shared";
import { KanbanTask, Toast } from "../../../../../widget";
import { TasksDashboardQueryFilters } from "../models";
import { buildFlatTasksRequest, buildTaskFilter, getVisibleStatuses, mapNodeToKanbanTask } from "../utils";
import { TASKS_DASHBOARD_PAGE_SIZE } from "./constants";

export interface UseTasksDashboardAgendaArgs {
  /** The one shared task-level filter object (BR-02828). Must be referentially stable. */
  filters: TasksDashboardQueryFilters;
  /** False unless the Gantt's Agenda is the view on screen, so no query is issued for the others. */
  enabled: boolean;
  pageSize?: number;
  refreshKey?: number;
}

export interface UseTasksDashboardAgendaReturnData {
  /** The rows loaded so far, in the server's deadline order. */
  agendaTasks: KanbanTask[];
  isLoadingAgendaTasks: boolean;
  hasMoreAgendaTasks: boolean;
  /** Loads the next page — the agenda's "Show more". */
  fetchMoreAgendaTasks: () => void;
}

const AGENDA_ERROR_MESSAGE = "Couldn't load tasks - please try again later.";

/**
 * The Gantt's Agenda view: the filtered task set flat and ordered by deadline, one page at a time.
 *
 * A separate query from the group-then-rows loading the timeline uses, because the agenda is a single
 * list across *every* object — assembling it from the groups would mean fetching all of their rows to
 * show the first twenty. Same shared filter object as the group headers and the leaf rows, so the three
 * views describe the same task set (BR-02828), and the same hide-toggle status restriction.
 */
export const useTasksDashboardAgenda = ({
  filters,
  enabled,
  pageSize = TASKS_DASHBOARD_PAGE_SIZE,
  refreshKey = 0,
}: UseTasksDashboardAgendaArgs): UseTasksDashboardAgendaReturnData => {
  const [tasks, setTasks] = useState<KanbanTask[]>([]);
  const [paging, setPaging] = useState<{ hasNextPage: boolean; endCursor: string | null }>({
    hasNextPage: false,
    endCursor: null,
  });
  const [isLoading, setIsLoading] = useState(false);

  const sharedFilter = useMemo(() => buildTaskFilter(filters), [filters]);

  // Monotonic id of the current query, so a response for a superseded filter — or a page whose cursor
  // belongs to the old one — is discarded rather than appended.
  const loadIdRef = useRef(0);

  /*
    Invalidated during render rather than when the response lands, so the rows on screen never belong to
    a filter the user has already changed (and don't stay there if the new request fails — the toast
    alone wouldn't say the list describes a filter that is no longer applied). A same-criteria reload
    (`refreshKey`, e.g. after the task panel saves) keeps them: they still describe the current
    selection.
  */
  const queryRef = useRef({ sharedFilter, refreshKey });
  if (queryRef.current.sharedFilter !== sharedFilter || queryRef.current.refreshKey !== refreshKey) {
    const criteriaChanged = queryRef.current.sharedFilter !== sharedFilter;

    queryRef.current = { sharedFilter, refreshKey };
    loadIdRef.current += 1;

    if (criteriaChanged) {
      setTasks([]);
      setPaging({ hasNextPage: false, endCursor: null });
      // Loading from this render, not from the effect that asks: in between, the rows are cleared with
      // nothing in flight, and the agenda would read as "no tasks match" under its new filter.
      if (enabled) setIsLoading(true);
    }
  }

  const searchTasks = useCallback(
    async (afterCursor: string | null): Promise<SearchWorkflowTasksResponse | undefined> => {
      try {
        // The hide toggles narrow the agenda exactly as they narrow the group headers.
        const visibleStatuses = getVisibleStatuses(filters);

        const response = await searchWorkflowTasks(
          buildFlatTasksRequest(sharedFilter, pageSize, afterCursor),
          visibleStatuses && { operator: "in", value: visibleStatuses }
        );

        if (response.status === Status.Success && response.data) return response.data;

        Toast.error({ message: AGENDA_ERROR_MESSAGE });
        return undefined;
      } catch {
        Toast.error({ message: AGENDA_ERROR_MESSAGE });
        return undefined;
      }
    },
    [sharedFilter, filters, pageSize]
  );

  const loadFirstPage = useCallback(async (): Promise<void> => {
    const loadId = loadIdRef.current;

    setIsLoading(true);

    const response = await searchTasks(null);

    if (loadId !== loadIdRef.current) return;

    if (response) {
      setTasks(response.results.map(mapNodeToKanbanTask));
      setPaging({ hasNextPage: response.paging.hasNextPage, endCursor: response.paging.endCursor || null });
    }

    setIsLoading(false);
  }, [searchTasks]);

  useEffect(() => {
    if (!enabled) return;

    loadFirstPage();
  }, [loadFirstPage, enabled, refreshKey]);

  // Read inside the paging callback so the cursor comes from current state without the request living
  // in a state updater (which React can invoke twice).
  const pagingRef = useRef(paging);
  pagingRef.current = paging;
  const isLoadingRef = useRef(isLoading);
  isLoadingRef.current = isLoading;

  const fetchMoreAgendaTasks = useCallback((): void => {
    const { hasNextPage, endCursor } = pagingRef.current;
    if (!hasNextPage || isLoadingRef.current) return;

    const loadId = loadIdRef.current;
    setIsLoading(true);

    searchTasks(endCursor).then((response) => {
      if (loadId !== loadIdRef.current) return;

      if (response) {
        setTasks((current) => [...current, ...response.results.map(mapNodeToKanbanTask)]);
        setPaging({ hasNextPage: response.paging.hasNextPage, endCursor: response.paging.endCursor || null });
      }

      setIsLoading(false);
    });
  }, [searchTasks]);

  return {
    agendaTasks: tasks,
    isLoadingAgendaTasks: isLoading,
    hasMoreAgendaTasks: paging.hasNextPage,
    fetchMoreAgendaTasks,
  };
};
